import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import { RootState } from "src/redux/reducers";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as Stars } from "src/images/ai-stars-white.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Column } from "src/redux/dictionary/types";
import { API_URLS } from "src/utils/API_URLS";
import { TaskType } from "src/types";
import { showToast } from "src/app/methods/showToast";
import { handleError } from "src/utils/error";
import { showErrorToast } from "src/utils/methods";
import DismissibleTooltip from "src/app/components/DismissibleTooltip/DismissibleTooltip";

const ASK_AI_TOOLTIP_KEY = "askAITooltipHiddenWorkspaceTasks" as const;
interface WorkspaceTasksAskAIButtonProps {
  taskType: TaskType | undefined;
  columns: Column[];
}

export default function WorkspaceTasksAskAIButton({
  taskType,
  columns,
}: WorkspaceTasksAskAIButtonProps) {
  const [
    createAndCalculateAiPoweredFieldLoading,
    setCreateAndCalculateAiPoweredFieldLoading,
  ] = useState(false);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const { selectedTasks } = useSelector(
    (state: RootState) => state.taskReducer,
  );

  const createAndCalculateAiPoweredField = async (col: Column) => {
    const tasksWithSelectedAIField = selectedTasks.filter((task) =>
      task.metadata.find(
        (field) => field.wsGlobalTaskMetaFieldUuid === col.uuid,
      ),
    );

    if (!tasksWithSelectedAIField.length) {
      showErrorToast({
        id: "ws_selected_tasks_dont_have_ai_fields",
        defaultMessage: "Selected tasks do not have AI fields",
      });
      return;
    }

    const taskProjectUuids = new Set(
      tasksWithSelectedAIField.map((task) => task.wsProjectUuid),
    );

    const requests = Array.from(taskProjectUuids).map((projectUuid) => {
      const wsTaskUuids = tasksWithSelectedAIField
        .filter((task) => task.wsProjectUuid === projectUuid)
        .map((task) => task.taskId);

      return axios.post(API_URLS.createGlobalTaskMetaFieldAndGenerateValue, {
        wsGlobalTaskMetaFieldUuid: col.uuid,
        wsProjectUuid: projectUuid,
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsTaskUuids,
        wsTaskType: taskType,
      });
    });

    setCreateAndCalculateAiPoweredFieldLoading(true);

    Promise.all(requests)
      .then(() => {
        showToast(
          "info",
          <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
          <>
            <IDHFormattedMessage
              id={"toast_the_ai_field_is_being_calculated"}
              defaultMessage={`The AI field is being calculated. The result will appear in the ${taskType} tab - column`}
            />
            <b> {col.name}. </b>
            <IDHFormattedMessage
              id={"toast_the_ai_field_this_may_take_a_moment"}
              defaultMessage={"This may take a moment"}
            />
          </>,
        );
      })
      .catch(handleError)
      .finally(() => {
        setTimeout(() => {
          setCreateAndCalculateAiPoweredFieldLoading(false);
        }, 1000);
      });
  };

  const aiPoweredMetaFields = columns.filter((col) => {
    const aiMetaValueService = col.data?.aiMetaValueService;
    return (
      typeof aiMetaValueService === "object" && aiMetaValueService !== null
    );
  });

  if (aiPoweredMetaFields.length === 0) return null;

  return (
    <>
      <DismissibleTooltip
        storageKey={ASK_AI_TOOLTIP_KEY}
        tooltipId="ask-ai-tooltip"
        title={
          <IDHFormattedMessage id="ws_ask_ai_title" defaultMessage="Ask AI" />
        }
        description={
          <IDHFormattedMessage
            id="ws_ask_ai_description"
            defaultMessage="Upload the campaign brief and take advantage of AI support."
          />
        }
        checkboxLabel={
          <IDHFormattedMessage
            id="ws_dont_show_me_again"
            defaultMessage="Don't show me again"
          />
        }
        confirmLabel={<IDHFormattedMessage id="ws_ok" defaultMessage="Ok" />}
      />
      <Dropdown toBottom>
        {(isOpen, setIsOpen) => (
          <>
            <Button
              size="large"
              variant="white"
              data-tooltip-id="ask-ai-tooltip"
              data-qa="ask-ai-button"
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <Stars />
              <IDHFormattedMessage id="ws_ask_ai" defaultMessage="Ask AI..." />
            </Button>

            <DropdownMenu isOpen={isOpen}>
              <div style={{ maxHeight: 207, overflow: "auto" }}>
                {aiPoweredMetaFields.map((field) => (
                  <DropdownMenuItem
                    key={field.uuid}
                    className="ws-button--white dropdown__menu-item--multiline"
                    data-qa={`ask-ai-field--${field.name}`}
                    onClick={() => createAndCalculateAiPoweredField(field)}
                    disabled={createAndCalculateAiPoweredFieldLoading}
                  >
                    <Stars />
                    <div>
                      <div className="title">{field.name}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenu>
          </>
        )}
      </Dropdown>
    </>
  );
}
